/**
 * Procurement data for the layout BOM: part numbers, unit costs, lead times,
 * and the rules that derive installation infrastructure (hangers, power rail,
 * controllers) from what is drawn.
 *
 * Every cost here is a rough-order-of-magnitude placeholder so a layout can be
 * totalled before quotes exist. Real prices are expected to arrive through the
 * toolbox catalog (Tools Configuration custom fields "Part Number" and
 * "Unit Cost") or per element (`customProperties.partNumber` / `unitCost`),
 * both of which take precedence over this book.
 */
import type { ToolboxCustomField } from '../types';

export type BomCurrency = 'USD';

export type BomPriceEntry = {
  partNumber: string;
  manufacturer: string;
  /** Unit cost in the book's currency. `null` marks an item the fab buys elsewhere. */
  unitCost: number | null;
  leadTime: string;
  description?: string;
};

/** How infrastructure quantities follow from the drawn track and fleet. */
export type BomInfrastructureRules = {
  /** One hanger (bracket + rod + anchor) per this many metres of track centreline. */
  hangerPitchMeters: number;
  /** Extra hangers at each curve, where the rail changes direction. */
  hangersPerCurve: number;
  /** Extra hangers at each junction or crossover. */
  hangersPerJunction: number;
  /** Stock length of one conductor bus bar section. */
  powerBusStockMeters: number;
  /** One power feed unit per this many metres of track, minimum one. */
  powerFeedSpacingMeters: number;
  /** One expansion joint per this many metres of track. */
  powerExpansionSpacingMeters: number;
  /** One zone controller per this many metres of track, minimum one. */
  zoneControllerSpanMeters: number;
};

export type LayoutBomPriceBook = {
  currency: BomCurrency;
  items: Record<string, BomPriceEntry>;
  infrastructure: BomInfrastructureRules;
};

export const DEFAULT_INFRASTRUCTURE_RULES: BomInfrastructureRules = {
  hangerPitchMeters: 1.5,
  hangersPerCurve: 2,
  hangersPerJunction: 2,
  powerBusStockMeters: 4,
  powerFeedSpacingMeters: 100,
  powerExpansionSpacingMeters: 50,
  zoneControllerSpanMeters: 60,
};

/**
 * Price keys are stable ids, independent of the display name a catalog gives a
 * tool. Track keys mirror the BOM row ids; everything else is a family.
 */
export const DEFAULT_BOM_PRICE_BOOK: LayoutBomPriceBook = {
  currency: 'USD',
  infrastructure: DEFAULT_INFRASTRUCTURE_RULES,
  items: {
    // Track
    'track-straight': {
      partNumber: 'AT1-2000',
      manufacturer: 'AeroTrak',
      unitCost: 1800,
      leadTime: '8–10 wks',
      description: 'Straight overhead rail section',
    },
    'track-curve45': {
      partNumber: 'AT3-R45',
      manufacturer: 'AeroTrak',
      unitCost: 2600,
      leadTime: '10–12 wks',
    },
    'track-curve90': {
      partNumber: 'AT2-R90',
      manufacturer: 'AeroTrak',
      unitCost: 3200,
      leadTime: '10–12 wks',
    },
    'track-curve180': {
      partNumber: 'AT4-R180',
      manufacturer: 'AeroTrak',
      unitCost: 5800,
      leadTime: '10–12 wks',
    },
    'track-connector': {
      partNumber: 'CN1-1000',
      manufacturer: 'AeroTrak',
      unitCost: 2400,
      leadTime: '8–10 wks',
    },
    'track-junction-circle': {
      partNumber: 'CJ1-ROT',
      manufacturer: 'AeroTrak',
      unitCost: 18500,
      leadTime: '14–16 wks',
      description: 'Rotary circular junction with drive',
    },
    'track-cross-rail': {
      partNumber: 'CX1-XOVER',
      manufacturer: 'AeroTrak',
      unitCost: 14000,
      leadTime: '14–16 wks',
    },
    'track-branch': {
      partNumber: 'BR1-SW',
      manufacturer: 'AeroTrak',
      unitCost: 9500,
      leadTime: '12–14 wks',
    },
    'track-lift': {
      partNumber: 'LT1-VERT',
      manufacturer: 'AeroTrak',
      unitCost: 12000,
      leadTime: '14–16 wks',
    },
    // Vehicles and storage
    oht: {
      partNumber: 'OHT-V1',
      manufacturer: 'BriteLab',
      unitCost: 85000,
      leadTime: '20–24 wks',
      description: 'Overhead hoist transport vehicle',
    },
    stocker: {
      partNumber: 'STK-STD',
      manufacturer: 'BriteLab',
      unitCost: 350000,
      leadTime: '24–30 wks',
    },
    stb: {
      partNumber: 'STB-1',
      manufacturer: 'BriteLab',
      unitCost: 28000,
      leadTime: '12–14 wks',
    },
    ohb: {
      partNumber: 'OHB-1',
      manufacturer: 'BriteLab',
      unitCost: 32000,
      leadTime: '12–14 wks',
    },
    lifter: {
      partNumber: 'LFT-V1',
      manufacturer: 'BriteLab',
      unitCost: 65000,
      leadTime: '16–20 wks',
    },
    // Process equipment is customer-supplied; it stays unpriced unless overridden.
    equipment: {
      partNumber: '—',
      manufacturer: 'Customer',
      unitCost: null,
      leadTime: '—',
    },
    // Structure placed explicitly on the canvas
    'support-pillar': {
      partNumber: 'SP-H3200',
      manufacturer: 'Structural',
      unitCost: 1200,
      leadTime: '6–8 wks',
    },
    'horizontal-beam': {
      partNumber: 'HB-C150',
      manufacturer: 'Structural',
      unitCost: 650,
      leadTime: '6–8 wks',
    },
    'suspension-rod': {
      partNumber: 'ROD-M12',
      manufacturer: 'Structural',
      unitCost: 85,
      leadTime: '4 wks',
    },
    // Derived infrastructure
    'infra-hanger-kit': {
      partNumber: 'HGR-200',
      manufacturer: 'AeroTrak',
      unitCost: 140,
      leadTime: '6 wks',
      description: 'Rail hanger bracket with clamp',
    },
    'infra-suspension-rod': {
      partNumber: 'ROD-M12',
      manufacturer: 'Structural',
      unitCost: 85,
      leadTime: '4 wks',
    },
    'infra-ceiling-anchor': {
      partNumber: 'ANC-M12',
      manufacturer: 'Structural',
      unitCost: 35,
      leadTime: '4 wks',
      description: 'Ceiling anchor pair',
    },
    'infra-power-bus': {
      partNumber: 'PWR-BUS-4000',
      manufacturer: 'Conductix',
      unitCost: 420,
      leadTime: '8 wks',
      description: 'Enclosed conductor bus bar section',
    },
    'infra-power-feed': {
      partNumber: 'PWR-FEED-30A',
      manufacturer: 'Conductix',
      unitCost: 3800,
      leadTime: '8 wks',
    },
    'infra-power-expansion': {
      partNumber: 'PWR-EXP',
      manufacturer: 'Conductix',
      unitCost: 260,
      leadTime: '8 wks',
    },
    'infra-power-collector': {
      partNumber: 'PWR-COL-4P',
      manufacturer: 'Conductix',
      unitCost: 1450,
      leadTime: '8 wks',
      description: 'Vehicle current collector assembly',
    },
    'infra-ctl-system': {
      partNumber: 'CTL-OCS',
      manufacturer: 'BriteLab',
      unitCost: 120000,
      leadTime: '16 wks',
      description: 'OHT control system server and software',
    },
    'infra-ctl-zone': {
      partNumber: 'CTL-ZONE',
      manufacturer: 'BriteLab',
      unitCost: 9800,
      leadTime: '12 wks',
      description: 'Track zone controller cabinet',
    },
    'infra-ctl-junction': {
      partNumber: 'CTL-JCT',
      manufacturer: 'BriteLab',
      unitCost: 4200,
      leadTime: '12 wks',
    },
    'infra-ctl-vehicle': {
      partNumber: 'CTL-VEH',
      manufacturer: 'BriteLab',
      unitCost: 6500,
      leadTime: '12 wks',
      description: 'Onboard vehicle controller and radio',
    },
    'infra-ctl-stocker': {
      partNumber: 'CTL-STK',
      manufacturer: 'BriteLab',
      unitCost: 7500,
      leadTime: '12 wks',
    },
  },
};

/** Map a BOM row id (and its spec) onto a price-book key. */
export function priceKeyForBomRow(rowId: string, specification: string): string {
  if (rowId.startsWith('track-') || rowId.startsWith('infra-')) return rowId;
  if (rowId === 'amhs-oht') return 'oht';
  if (rowId.startsWith('stocker-')) return 'stocker';
  if (rowId.startsWith('equipment-')) {
    const spec = specification.toLowerCase();
    if (spec === 'stb' || spec.includes('side track buffer')) return 'stb';
    if (spec === 'ohb' || spec.includes('overhead hoist buffer')) return 'ohb';
    if (spec === 'lifter' || spec.includes('vertical lifter')) return 'lifter';
    return 'equipment';
  }
  if (rowId.startsWith('other-')) {
    const spec = specification.toLowerCase();
    if (spec.includes('pillar')) return 'support-pillar';
    if (spec.includes('beam')) return 'horizontal-beam';
    if (spec.includes('rod')) return 'suspension-rod';
  }
  return rowId;
}

const PART_NUMBER_FIELD = /^(part\s*(number|no\.?|#)|pn|sku)$/i;
const UNIT_COST_FIELD = /^(unit\s*(cost|price)|cost|price)$/i;

export function parseMoney(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string') return null;
  const cleaned = value.replace(/[^0-9.-]/g, '');
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** Part number and unit cost a catalog tool declares through its custom fields. */
export function readCatalogProcurement(fields: readonly ToolboxCustomField[] | undefined): {
  partNumber?: string;
  unitCost?: number;
} {
  const out: { partNumber?: string; unitCost?: number } = {};
  for (const field of fields ?? []) {
    const label = String(field.label ?? '').trim();
    if (!label) continue;
    if (out.partNumber == null && PART_NUMBER_FIELD.test(label)) {
      const value = String(field.value ?? '').trim();
      if (value) out.partNumber = value;
    } else if (out.unitCost == null && UNIT_COST_FIELD.test(label)) {
      const parsed = parseMoney(field.value);
      if (parsed != null) out.unitCost = parsed;
    }
  }
  return out;
}

export type BomProcurementOverride = {
  partNumber?: string;
  unitCost?: number;
};

export type BomCostSource = 'element' | 'catalog' | 'price-book' | 'none';

export type BomProcurement = {
  manufacturer: string;
  partNumber: string;
  leadTime: string;
  unitCostValue: number | null;
  costSource: BomCostSource;
};

/**
 * Resolve procurement for one BOM row. Precedence, most specific first:
 * element properties, catalog custom fields or SKU, then the price book.
 */
export function resolveBomProcurement(params: {
  rowId: string;
  specification: string;
  priceBook: LayoutBomPriceBook;
  element?: BomProcurementOverride;
  catalog?: BomProcurementOverride & { sku?: string };
}): BomProcurement {
  const key = priceKeyForBomRow(params.rowId, params.specification);
  const book = params.priceBook.items[key];

  const partNumber =
    params.element?.partNumber?.trim() ||
    params.catalog?.partNumber?.trim() ||
    params.catalog?.sku?.trim() ||
    book?.partNumber ||
    '—';

  let unitCostValue: number | null = null;
  let costSource: BomCostSource = 'none';
  if (params.element?.unitCost != null) {
    unitCostValue = params.element.unitCost;
    costSource = 'element';
  } else if (params.catalog?.unitCost != null) {
    unitCostValue = params.catalog.unitCost;
    costSource = 'catalog';
  } else if (book?.unitCost != null) {
    unitCostValue = book.unitCost;
    costSource = 'price-book';
  }

  return {
    manufacturer: book?.manufacturer ?? '—',
    partNumber,
    leadTime: book?.leadTime ?? '—',
    unitCostValue,
    costSource,
  };
}

const currencyFormatters = new Map<BomCurrency, Intl.NumberFormat>();

export function formatBomCurrency(value: number | null | undefined, currency: BomCurrency): string {
  if (value == null || !Number.isFinite(value)) return '—';
  let formatter = currencyFormatters.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });
    currencyFormatters.set(currency, formatter);
  }
  return formatter.format(value);
}

export type BomInfrastructureInput = {
  /** Straight plus curved rail centreline, in metres. */
  trackLengthMeters: number;
  curveCount: number;
  junctionCount: number;
  ohtCount: number;
  stockerCount: number;
  /** Suspension rods already drawn on the canvas, so they are not counted twice. */
  explicitSuspensionRods: number;
};

export type BomInfrastructureLine = {
  id: string;
  component: string;
  specification: string;
  quantity: number;
  unit: string;
  totalLengthMeters: number | null;
  /** Plain-language rule the quantity came from, shown as the row's basis. */
  derivedFrom: string;
};

/**
 * Supports, power rail and controllers implied by the drawn layout. Each line
 * states its rule so the number can be checked against the design standard.
 */
export function deriveInfrastructureLines(
  input: BomInfrastructureInput,
  rules: BomInfrastructureRules = DEFAULT_INFRASTRUCTURE_RULES
): BomInfrastructureLine[] {
  const lines: BomInfrastructureLine[] = [];
  const track = Math.max(0, input.trackLengthMeters);
  const hasTrack = track > 0 || input.junctionCount > 0 || input.curveCount > 0;

  if (hasTrack) {
    const hangers =
      Math.ceil(track / rules.hangerPitchMeters) +
      input.curveCount * rules.hangersPerCurve +
      input.junctionCount * rules.hangersPerJunction;
    const rods = Math.max(0, hangers - Math.max(0, input.explicitSuspensionRods));
    lines.push({
      id: 'infra-hanger-kit',
      component: 'Rail Hanger Kit',
      specification: 'Bracket + clamp',
      quantity: hangers,
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: `1 per ${rules.hangerPitchMeters} m of track, +${rules.hangersPerCurve} per curve, +${rules.hangersPerJunction} per junction`,
    });
    if (rods > 0) {
      lines.push({
        id: 'infra-suspension-rod',
        component: 'Suspension Rod',
        specification: 'M12 threaded rod',
        quantity: rods,
        unit: 'pcs',
        totalLengthMeters: null,
        derivedFrom:
          input.explicitSuspensionRods > 0
            ? `1 per hanger, less ${input.explicitSuspensionRods} already drawn`
            : '1 per hanger',
      });
    }
    lines.push({
      id: 'infra-ceiling-anchor',
      component: 'Ceiling Anchor Pair',
      specification: 'M12 anchors',
      quantity: hangers,
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: '1 pair per hanger',
    });
  }

  if (track > 0) {
    lines.push({
      id: 'infra-power-bus',
      component: 'Power Rail Bus Bar',
      specification: `${rules.powerBusStockMeters} m section`,
      quantity: Math.ceil(track / rules.powerBusStockMeters),
      unit: 'pcs',
      totalLengthMeters: track,
      derivedFrom: `Track centreline ÷ ${rules.powerBusStockMeters} m stock, rounded up`,
    });
    lines.push({
      id: 'infra-power-feed',
      component: 'Power Feed Unit',
      specification: '30 A feed',
      quantity: Math.max(1, Math.ceil(track / rules.powerFeedSpacingMeters)),
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: `1 per ${rules.powerFeedSpacingMeters} m of track, minimum 1`,
    });
    const expansions = Math.floor(track / rules.powerExpansionSpacingMeters);
    if (expansions > 0) {
      lines.push({
        id: 'infra-power-expansion',
        component: 'Power Rail Expansion Joint',
        specification: 'Thermal expansion joint',
        quantity: expansions,
        unit: 'pcs',
        totalLengthMeters: null,
        derivedFrom: `1 per ${rules.powerExpansionSpacingMeters} m of track`,
      });
    }
  }

  if (input.ohtCount > 0) {
    lines.push({
      id: 'infra-power-collector',
      component: 'Current Collector Assembly',
      specification: '4-pole collector',
      quantity: input.ohtCount,
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: '1 per OHT vehicle',
    });
  }

  if (hasTrack || input.ohtCount > 0) {
    lines.push({
      id: 'infra-ctl-system',
      component: 'OHT Control System',
      specification: 'OCS server + software',
      quantity: 1,
      unit: 'set',
      totalLengthMeters: null,
      derivedFrom: '1 per layout',
    });
  }
  if (track > 0) {
    lines.push({
      id: 'infra-ctl-zone',
      component: 'Zone Controller',
      specification: 'Track zone cabinet',
      quantity: Math.max(1, Math.ceil(track / rules.zoneControllerSpanMeters)),
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: `1 per ${rules.zoneControllerSpanMeters} m of track, minimum 1`,
    });
  }
  if (input.junctionCount > 0) {
    lines.push({
      id: 'infra-ctl-junction',
      component: 'Junction Controller',
      specification: 'Rotary / crossover drive controller',
      quantity: input.junctionCount,
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: '1 per junction or crossover',
    });
  }
  if (input.ohtCount > 0) {
    lines.push({
      id: 'infra-ctl-vehicle',
      component: 'Vehicle Controller',
      specification: 'Onboard controller + radio',
      quantity: input.ohtCount,
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: '1 per OHT vehicle',
    });
  }
  if (input.stockerCount > 0) {
    lines.push({
      id: 'infra-ctl-stocker',
      component: 'Stocker Controller',
      specification: 'Crane + port controller',
      quantity: input.stockerCount,
      unit: 'pcs',
      totalLengthMeters: null,
      derivedFrom: '1 per stocker',
    });
  }

  return lines;
}
